import { useEffect } from "react";
import { z } from "zod";
import { useLocationProperty } from "wouter/use-browser-location";

// Per-URL scroll memory for a round page's content scroller, restored on
// history pops (browser Back/Forward and the iPad header's back chevron,
// which calls `history.back()`).
//
// The funder workspace has its own richer landing model layered on these
// primitives (`use-funder-workspace-scroll-restoration.ts`) because its one
// stable <main> is reused across rails and must arbitrate hash landings,
// semantic targets, and quote anchors. Every other round page owns a
// per-route scroller that remounts at the top naturally, so all it needs is:
// save the offset per URL while scrolling, and put it back on a pop.
//
// Pages whose real reading position lives inside a nested scroll box (the
// wide member-column tables on Investigations and Recusals scroll both ways
// inside a max-height box, so the page scroller barely moves) mount
// `useNestedScrollRestoration` on that box as well. It shares the store and
// pop rules, keyed per container name so a page's boxes can't clobber each
// other or the page entry. Entries carry both axes; the horizontal offset
// matters for exactly these wide tables.

const maxStoredAgeMs = 30 * 60 * 1000;
const maxEntries = 30;
const restoreDeadlineMs = 3000;
const saveDebounceMs = 150;

const entrySchema = z.object({
  top: z.number().nonnegative(),
  // Absent on entries written before horizontal offsets were stored.
  left: z.number().nonnegative().optional(),
  createdAt: z.number(),
});

type ScrollStore = Record<string, ScrollEntry>;
export type ScrollEntry = z.infer<typeof entrySchema>;

// Drop expired entries, then oldest entries beyond the cap.
export function pruneScrollStore(store: ScrollStore, now: number): ScrollStore {
  const fresh = Object.entries(store).filter(
    ([, entry]) => now - entry.createdAt <= maxStoredAgeMs,
  );
  fresh.sort((a, b) => b[1].createdAt - a[1].createdAt);
  return Object.fromEntries(fresh.slice(0, maxEntries));
}

export function getCurrentScrollLocation() {
  // Hash included so a passage-deep-link visit never shares an entry with
  // the plain URL.
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

// Store key for a named nested scroll box's entry. Page locations always
// start with "/", so the prefix can never collide with a page entry.
export function nestedEntryLocation(
  containerKey: string,
  location: string,
): string {
  return `container:${containerKey}:${location}`;
}

/** Whether a stored entry exists and is within the retention window. */
export function isFreshScrollEntry(
  entry: ScrollEntry | undefined,
  now: number,
): entry is ScrollEntry {
  return entry != null && now - entry.createdAt <= maxStoredAgeMs;
}

export function loadScrollStore(storageKey: string): ScrollStore {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(storageKey);
  } catch {
    return {};
  }
  if (!raw) return {};
  // Parse per entry so one invalid record can't drop the whole store.
  try {
    const record = z.record(z.string(), z.unknown()).safeParse(JSON.parse(raw));
    if (!record.success) return {};
    const store: ScrollStore = {};
    for (const [location, value] of Object.entries(record.data)) {
      const entry = entrySchema.safeParse(value);
      if (entry.success) store[location] = entry.data;
    }
    return store;
  } catch {
    return {};
  }
}

export function saveScrollEntry(
  storageKey: string,
  location: string,
  top: number,
  left = 0,
) {
  const now = Date.now();
  const store = pruneScrollStore(loadScrollStore(storageKey), now);
  store[location] = { top, left, createdAt: now };
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(store));
  } catch {
    // Scrolling must not depend on session storage availability.
  }
}

// iOS rubber-banding reports scrollTops outside the valid range; a negative
// value would poison the stored entry, an over-max one could never restore.
export function clampScrollTop(el: {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}): number {
  return Math.max(0, Math.min(el.scrollTop, el.scrollHeight - el.clientHeight));
}

export function clampScrollLeft(el: {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}): number {
  return Math.max(0, Math.min(el.scrollLeft, el.scrollWidth - el.clientWidth));
}

// Debounced per-URL offset buffer. Both the location and the offset are
// captured at sample time — a debounced flush can land after a navigation
// and must not write the old offset under the new URL — and a sample under a
// new URL flushes the previous page's pending offset instead of clobbering
// it (the post-navigation restore itself fires a scroll event).
export function createPendingSaveBuffer(
  save: (location: string, top: number, left: number) => void,
  debounceMs = saveDebounceMs,
) {
  let pending: { location: string; top: number; left: number } | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = () => {
    if (timer != null) clearTimeout(timer);
    timer = null;
    if (pending == null) return;
    save(pending.location, pending.top, pending.left);
    pending = null;
  };

  return {
    sample(location: string, top: number, left = 0) {
      if (pending != null && pending.location !== location) flush();
      if (timer != null) clearTimeout(timer);
      pending = { location, top, left };
      timer = setTimeout(flush, debounceMs);
    },
    // Flushes any pending offset; safe to call repeatedly.
    dispose: flush,
  };
}

// Pop detection: a timestamp, not a flag — a hash-only pop never triggers the
// restore effect, so a flag would leak onto the next ordinary navigation. A
// push cancels a pending pop (wouter patches history.pushState to emit that
// event); replaceState doesn't, so replace-right-after-pop redirects still
// restore.
export const popFreshnessMs = 300;
export function createPopTracker() {
  let lastPopAt = 0;
  return {
    markPop(now = Date.now()) {
      lastPopAt = now;
    },
    cancelPop() {
      lastPopAt = 0;
    },
    isFreshPop(now = Date.now()) {
      return now - lastPopAt < popFreshnessMs;
    },
  };
}

const popTracker = createPopTracker();
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => popTracker.markPop());
  window.addEventListener("pushState", () => popTracker.cancelPop());
}

/** Whether the current location change arrived via Back/Forward. */
export function isFreshHistoryPop(now = Date.now()): boolean {
  return popTracker.isFreshPop(now);
}

// The offset to restore, or null to stand down. Restore only applies a saved,
// unexpired offset on a history pop; everything else leaves the scroller
// where the remounting route put it (the top). Hash URLs belong to their
// landing owner (passage links), and a quote request lands via its own
// anchored placement.
//
// A pop to a URL with NO entry preserves the current viewport rather than
// forcing the top. That looks wrong for a never-scrolled tab variant (the
// neighbor's offset carries over), but it is required for URL-backed dialog
// entries (use-url-dialog.ts): the dialog blocks the scroller while its URL
// is current, so "no entry" there means the page underneath never moved, and
// a forced top would dump it under the reopened dialog. Cross-page pops are
// unaffected either way — per-route scrollers remount at the top.
export function resolvePageRestoreEntry(
  entry: ScrollEntry | undefined,
  now: number,
  {
    hash,
    hasQuoteParam,
    isHistoryPop,
  }: { hash: string; hasQuoteParam: boolean; isHistoryPop: boolean },
): ScrollEntry | null {
  if (!isHistoryPop) return null;
  if (hash !== "") return null;
  if (hasQuoteParam) return null;
  if (!isFreshScrollEntry(entry, now)) return null;
  return entry;
}

export function resolvePageRestoreTop(
  entry: ScrollEntry | undefined,
  now: number,
  context: { hash: string; hasQuoteParam: boolean; isHistoryPop: boolean },
): number | null {
  return resolvePageRestoreEntry(entry, now, context)?.top ?? null;
}

function currentRestoreContext() {
  return {
    hash: window.location.hash,
    hasQuoteParam: new URLSearchParams(window.location.search).has("quote"),
    isHistoryPop: isFreshHistoryPop(),
  };
}

function getPageScrollStorageKey(roundId: string) {
  return `s-process:page-scroll:${roundId}`;
}

/**
 * The offset the hook's restore effect will apply for the CURRENT location,
 * or null when it stands down. For page-local scroll owners (e.g. the
 * proposal page's per-tab click memory) that must yield to a history restore
 * but still cover the URLs it has no entry for.
 */
export function peekHistoryRestoreTop(roundId: string): number | null {
  return resolvePageRestoreTop(
    loadScrollStore(getPageScrollStorageKey(roundId))[
      getCurrentScrollLocation()
    ],
    Date.now(),
    currentRestoreContext(),
  );
}

/**
 * Per-URL scroll save + pop restore for a round page's content scroller.
 *
 * The container may be rendered by the page itself (pass a ref-reading
 * closure) or by the round shell around it (pass a selector-reading closure).
 * Content mounting async is fine: the restore retries until the container is
 * tall enough to hold the offset, bounded at a few seconds, and yields
 * immediately if the user starts scrolling first.
 */
export function usePageScrollRestoration({
  roundId,
  getContainer,
}: {
  roundId: string;
  getContainer: () => HTMLElement | null;
}): void {
  useScrollRestorationCore({
    storageKey: getPageScrollStorageKey(roundId),
    getEntryLocation: getCurrentScrollLocation,
    getContainer,
  });
}

/**
 * Per-URL scroll save + pop restore for a nested scroll box a page owns in
 * addition to its content scroller (e.g. the wide member-column tables on
 * Investigations and Recusals, which scroll both ways inside a max-height
 * box). Shares the page store and the pop/stand-down rules; `containerKey`
 * names the box so a page with several boxes keeps distinct entries. Both
 * `containerKey` and `getContainer` must be stable for the component's life.
 */
export function useNestedScrollRestoration({
  roundId,
  containerKey,
  getContainer,
}: {
  roundId: string;
  containerKey: string;
  getContainer: () => HTMLElement | null;
}): void {
  useScrollRestorationCore({
    storageKey: getPageScrollStorageKey(roundId),
    getEntryLocation: () =>
      nestedEntryLocation(containerKey, getCurrentScrollLocation()),
    getContainer,
  });
}

function useScrollRestorationCore({
  storageKey,
  getEntryLocation,
  getContainer,
}: {
  storageKey: string;
  getEntryLocation: () => string;
  getContainer: () => HTMLElement | null;
}): void {
  // Hash included (and subscribed): backing out of a passage-hash entry is a
  // hash-only pop that wouter's pathname/search hooks don't re-render on.
  const locationKey = useLocationProperty(
    () =>
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    undefined,
  );

  // Continuous save. Re-runs per URL so a container recreated by a route
  // remount gets a fresh listener.
  useEffect(() => {
    const container = getContainer();
    if (!container) return;

    const buffer = createPendingSaveBuffer((entryLocation, top, left) =>
      saveScrollEntry(storageKey, entryLocation, top, left),
    );
    const onScroll = () =>
      buffer.sample(
        getEntryLocation(),
        clampScrollTop(container),
        clampScrollLeft(container),
      );

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      buffer.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getContainer and getEntryLocation are stable accessors; the locationKey re-bind covers route remounts swapping the container element.
  }, [storageKey, locationKey]);

  useEffect(() => {
    const container = getContainer();
    if (!container) return;

    // A saved 0 is a real offset: query-only history hops reuse the mounted
    // scroller, so a page left at the top must be put back at the top rather
    // than keeping the neighboring URL's offset.
    const entry = resolvePageRestoreEntry(
      loadScrollStore(storageKey)[getEntryLocation()],
      Date.now(),
      currentRestoreContext(),
    );
    if (entry == null) return;
    const top = entry.top;
    // The horizontal offset rides along with the height-gated restore: rows
    // and columns mount together, and a stale-wide offset clamps at apply
    // time rather than holding the whole restore hostage.
    const left = entry.left ?? 0;

    // Content mounts async; wait until the saved offset is reachable, bounded,
    // and yield to the user's own scrolling.
    const deadline = Date.now() + restoreDeadlineMs;
    let raf: number | null = null;
    let cancelled = false;
    const cancel = () => {
      if (cancelled) return;
      cancelled = true;
      container.removeEventListener("wheel", cancel);
      container.removeEventListener("touchstart", cancel);
    };
    container.addEventListener("wheel", cancel, { passive: true });
    container.addEventListener("touchstart", cancel, { passive: true });

    const attempt = () => {
      if (cancelled) return;
      if (Date.now() > deadline) {
        cancel();
        return;
      }
      if (container.scrollHeight >= top + container.clientHeight) {
        container.scrollTop = top;
        if (left > 0) {
          container.scrollLeft = Math.min(
            left,
            container.scrollWidth - container.clientWidth,
          );
        }
        cancel();
        return;
      }
      raf = window.requestAnimationFrame(attempt);
    };
    attempt();

    return () => {
      cancel();
      if (raf != null) window.cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getContainer and getEntryLocation are stable accessors; the restore decision belongs to each URL change.
  }, [storageKey, locationKey]);
}
